import { memo } from 'react';
import { deleteIconSm, editIconSm, viewIconSm } from '@tpct/icons';
import { __ } from '../i18n/vsme';
import type { StoredFile } from './fileStoreTypes';

export type FileStoreView = 'list' | 'grid';

export type FileStoreTableProps = {
  files: readonly StoredFile[];
  view: FileStoreView;
  onViewChange: (view: FileStoreView) => void;
  /** Accessible name of the table (list) or the card list (grid). */
  label: string;
  /** The owner's delete right (the list's `rights.can_delete`); a row also needs its own `can_delete`. */
  canDelete: boolean;
  onPreview: (file: StoredFile) => void;
  onDelete: (file: StoredFile) => void;
  /** Relabel action; omitted = no edit button (no deposit right). */
  onEdit?: (file: StoredFile) => void;
  /** Read status this screen knows better than the last answer (an import it is running). */
  statusOverrides?: ReadonlyMap<number, string>;
  /** Runs the read again; the button shows only on rows `canReread` accepts. */
  onReread?: (file: StoredFile) => void;
  canReread?: (file: StoredFile) => boolean;
  /** Info line under a row's read badge (why a file is not read, …); `null` = none. */
  rowNote?: (file: StoredFile) => string | null;
  emptyText: string;
};

/** FEC/DSN read badge: the artifact's status as the user reads it; a status this screen does not know reads « non lu ». */
export const artifactStatusLabel = (status: string): string => {
  switch (status) {
    case 'importing':
      return __('lecture en cours');
    case 'imported':
      return __('lu');
    case 'failed':
      return __('lecture échouée');
    default:
      return __('non lu');
  }
};

const fileStatus = (file: StoredFile, overrides: ReadonlyMap<number, string> | undefined): string | null => (
  overrides?.get(file.id) ?? file.artifact?.status ?? null
);

type FileActionsProps = {
  file: StoredFile;
  canDelete: boolean;
  onPreview: (file: StoredFile) => void;
  onDelete: (file: StoredFile) => void;
  onEdit?: (file: StoredFile) => void;
  /** Set only on rows whose read may run again. */
  onReread?: (file: StoredFile) => void;
};

/** The drawn icon; the button's `aria-label` names it, the hidden text keeps a copy for search and print. */
const ActionIcon = ({ src, text }: { src: string; text: string }) => (
  <>
    <img src={src} alt="" aria-hidden="true" className="file-store-table__action-icon" />
    <span className="visually-hidden">{text}</span>
  </>
);

const FileActions = ({ file, canDelete, onPreview, onDelete, onEdit, onReread }: FileActionsProps) => (
  <span className="file-store-table__actions">
    <button
      type="button"
      className="file-store-table__action file-store-table__action--icon file-store-table__action--preview"
      aria-label={`${__('Prévisualiser')} ${file.name}`}
      title={__('Prévisualiser')}
      onClick={() => onPreview(file)}
    >
      <ActionIcon src={viewIconSm} text={__('Voir')} />
    </button>
    {onEdit !== undefined && (
      <button
        type="button"
        className="file-store-table__action file-store-table__action--icon file-store-table__action--edit"
        aria-label={`${__('Modifier')} ${file.name}`}
        title={__('Modifier')}
        onClick={() => onEdit(file)}
      >
        <ActionIcon src={editIconSm} text={__('Modifier')} />
      </button>
    )}
    {onReread !== undefined && (
      <button
        type="button"
        className="file-store-table__action file-store-table__action--reread"
        aria-label={`${__('Relancer la lecture')} ${file.name}`}
        onClick={() => onReread(file)}
      >
        {__('Relancer la lecture')}
      </button>
    )}
    {canDelete && file.can_delete && (
      <button
        type="button"
        className="file-store-table__action file-store-table__action--icon file-store-table__action--delete"
        aria-label={`${__('Supprimer')} ${file.name}`}
        title={__('Supprimer')}
        onClick={() => onDelete(file)}
      >
        <ActionIcon src={deleteIconSm} text={__('Supprimer')} />
      </button>
    )}
  </span>
);

const StatusBadge = ({ status }: { status: string | null }) => (status === null
  ? null
  : <span className={`file-store-table__status file-store-table__status--${status}`}>{artifactStatusLabel(status)}</span>);

const RowNote = ({ note }: { note: string | null }) => (note === null
  ? null
  : <span className="file-store-table__note">{note}</span>);

type FileItemProps = FileActionsProps & { status: string | null; note: string | null };

const FileListRow = memo(({ file, status, note, ...actions }: FileItemProps) => (
  <tr className="file-store-table__row">
    <td className="file-store-table__name">{file.name}</td>
    <td>{file.type_label}</td>
    <td>{file.concerned_year === null ? '—' : String(file.concerned_year)}</td>
    <td>{file.entity.label}</td>
    <td>{file.provenance.label}</td>
    <td>
      <StatusBadge status={status} />
      <RowNote note={note} />
    </td>
    <td><FileActions file={file} {...actions} /></td>
  </tr>
));

FileListRow.displayName = 'FileListRow';

const FileCard = memo(({ file, status, note, ...actions }: FileItemProps) => (
  <li className="file-store-table__card">
    <span className="file-store-table__name">{file.name}</span>
    <span className="file-store-table__meta">
      {`${file.type_label} · ${file.concerned_year === null ? '—' : String(file.concerned_year)} · ${file.entity.label}`}
    </span>
    <StatusBadge status={status} />
    <RowNote note={note} />
    <FileActions file={file} {...actions} />
  </li>
));

FileCard.displayName = 'FileCard';

/**
 * One owner's stored files, as a table or as cards. Purely presentational: the consumer owns the
 * list request, the rights and the dialogs; a row re-renders only when its own file changes.
 */
const FileStoreTable = ({
  files,
  view,
  onViewChange,
  label,
  canDelete,
  onPreview,
  onDelete,
  onEdit,
  statusOverrides,
  onReread,
  canReread,
  rowNote,
  emptyText,
}: FileStoreTableProps) => {
  const itemProps = (file: StoredFile): FileItemProps => ({
    file,
    status: fileStatus(file, statusOverrides),
    note: rowNote?.(file) ?? null,
    canDelete,
    onPreview,
    onDelete,
    onEdit,
    onReread: onReread !== undefined && canReread?.(file) === true ? onReread : undefined,
  });

  const renderFiles = () => {
    if (files.length === 0) {
      return <p className="file-store-table__empty">{emptyText}</p>;
    }
    if (view === 'grid') {
      return (
        <ul className="file-store-table__grid" aria-label={label}>
          {files.map((file) => <FileCard key={file.id} {...itemProps(file)} />)}
        </ul>
      );
    }
    return (
      <table className="file-store-table__table" aria-label={label}>
        <thead>
          <tr>
            <th scope="col">{__('Nom')}</th>
            <th scope="col">{__('Type')}</th>
            <th scope="col">{__('Année')}</th>
            <th scope="col">{__('Entité')}</th>
            <th scope="col">{__('Provenance')}</th>
            <th scope="col">{__('Lecture')}</th>
            <th scope="col">{__('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => <FileListRow key={file.id} {...itemProps(file)} />)}
        </tbody>
      </table>
    );
  };

  return (
    <div className="file-store-table">
      <div className="file-store-table__views" role="group" aria-label={__('Affichage')}>
        <button type="button" aria-pressed={view === 'list'} onClick={() => onViewChange('list')}>
          {__('Liste')}
        </button>
        <button type="button" aria-pressed={view === 'grid'} onClick={() => onViewChange('grid')}>
          {__('Grille')}
        </button>
      </div>
      {renderFiles()}
    </div>
  );
};

export default FileStoreTable;
